import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Events } from '@pncl/common-models';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { finalize, map, catchError, tap, take } from 'rxjs/operators';
import { IFilterAPI } from 'src/app/standalones/components/generic-filters-view/filters.interface';
import { httpOptionsWithSkipErrorInterceptor } from '../common/functions-and-constant/interceptor';
import { TOAST_CLASSES } from '../common/utils/notification-constants';
import { Program, ProgramStats } from '../models/program';
import { User } from '../models/user';
import { URLService } from './dynamic-url.service';
import { ConfirmationModalConfig, ModalManagerService } from './modal-manager.service';
import { NotificationToasterService } from './notification-toaster.service';
import { UserService } from './user.service';
import { ProfilesService } from './profiles.service';
import { Personas } from './acl.service';

// This is the only filter we need for now, but we can easily expand later
// and apply other filters already implemented in the IFilterAPI
type ProgramFilters = Pick<IFilterAPI, 'sites'>;

@Injectable({
  providedIn: 'root',
})
export class ProgramService {
  ProgramStatus = Events.Programs.ProgramStatus;

  readonly programStats$ = new BehaviorSubject<ProgramStats | null>(null);
  readonly isStatsLoading$ = new BehaviorSubject<boolean>(false);

  // Public map for direct template access
  readonly statusDisplayMap: Record<string, string>;

  // Simple in-memory cache: once we fetch site admins for a site, reuse them for the session.
  private readonly siteAdminsBySiteId = new Map<string, User[]>();

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private translateService: TranslateService,
    private modalManagerService: ModalManagerService,
    private userService: UserService,
    private profilesService: ProfilesService,
    private notificationService: NotificationToasterService,
    private notificationToasterService: NotificationToasterService,
  ) {
    // Initialize the status display map
    this.statusDisplayMap = {
      [Events.Programs.ProgramStatus.DRAFT]: this.translateService.instant('DRAFT'),
      [Events.Programs.ProgramStatus.IN_REVIEW]: this.translateService.instant('IN REVIEW'),
      [Events.Programs.ProgramStatus.PUBLISHED]: this.translateService.instant('PUBLISHED'),
    };
  }

  /**
   * Creates a new program.
   * @param programData - The data for the program to be created.
   * @returns An Observable of the HTTP response.
   */
  createProgram(programData: Program): Observable<Program> {
    return this.http
      .post<any>(
        `${this.urlService.getDynamicUrl()}/programs`,
        programData,
        httpOptionsWithSkipErrorInterceptor,
      )
      .pipe(map((response) => new Program(response)));
  }

  /**
   * Updates an existing program.
   * @param programData - The data for the program to be updated.
   * @returns An Observable of the HTTP response.
   */
  updateProgram(programData: Partial<Program>): Observable<Program> {
    return this.http
      .patch<any>(
        `${this.urlService.getDynamicUrl()}/programs/${programData._id}`,
        programData,
        httpOptionsWithSkipErrorInterceptor,
      )
      .pipe(map((response) => new Program(response)));
  }

  /** Pull any programs that match the given filters.
   *  • Pass nothing → backend returns “all accessible programs according to the user's role”.
   *  • Pass { sites: [siteId] } → programs for that site only.
   *  • Easily extend to other filter fields later (status, date-range …).
   */
  getPrograms(filters?: ProgramFilters): Observable<Program[]> {
    let params = new HttpParams();

    if (filters && Object.keys(filters).length) {
      const encodedFilters = btoa(JSON.stringify(filters));
      params = params.set('filters', encodedFilters);
    }

    return this.http
      .get<any>(`${this.urlService.getDynamicUrl()}/programs`, {
        params,
        ...httpOptionsWithSkipErrorInterceptor,
      })
      .pipe(map((response) => response.programs.map((program: any) => new Program(program))));
  }

  /**
   * Publishes a draft program.
   * @param programId - The ID of the program to publish
   * @returns Observable<Program> of the updated (published) program
   */
  publishProgram(programId: string): Observable<Program> {
    return this.http
      .post<any>(
        `${this.urlService.getDynamicUrl()}/programs/${programId}/publish`,
        {},
        httpOptionsWithSkipErrorInterceptor,
      )
      .pipe(map((response) => new Program(response.program)));
  }

  /**
   * Fetches calculated statistics for a program including scheduled and actual dosage.
   * @param programId - The ID of the program to fetch stats for
   * @returns Observable of ProgramStats containing scheduledDays, scheduledDosageMinutes, and actualDosageMinutes
   */
  getProgramStats(programId: string): Observable<ProgramStats> {
    const url = `${this.urlService.getDynamicUrl()}/programs/${programId}/stats`;
    return this.http
      .get<any>(url, httpOptionsWithSkipErrorInterceptor)
      .pipe(map((response) => new ProgramStats(response)));
  }

  /**
   * Refreshes program stats and emits to programStats$ BehaviorSubject
   * @param programId - The ID of the program to refresh stats for
   */
  refreshProgramStats(programId: string): void {
    this.isStatsLoading$.next(true);
    this.getProgramStats(programId)
      .pipe(
        take(1),
        finalize(() => this.isStatsLoading$.next(false)),
      )
      .subscribe({
        next: (stats) => {
          this.programStats$.next(stats);
        },
        error: () => {
          this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translateService.instant('Failed to load program stats'),
            '',
            this.notificationToasterService.generateNotificationCode(),
          );
          this.programStats$.next(new ProgramStats({}));
        },
      });
  }

  /**
   * Deletes a program, applies only to draft programs and published programs that have not been started yet.
   * @param programId - The ID of the program to be deleted.
   * @returns An Observable of the HTTP response.
   */
  deleteProgram(programId: string): Observable<any> {
    return this.http.delete(
      `${this.urlService.getDynamicUrl()}/programs/${programId}`,
      httpOptionsWithSkipErrorInterceptor,
    );
  }

  /**
   * Archives a published and started program.
   * @param programId - The ID of the program to be archived.
   * @returns An Observable of the HTTP response.
   */
  archiveProgram(programId: string): Observable<Program> {
    return this.http
      .post<any>(
        `${this.urlService.getDynamicUrl()}/programs/${programId}/archive`,
        {},
        httpOptionsWithSkipErrorInterceptor,
      )
      .pipe(map((response) => new Program(response.program)));
  }

  /**
   * Shows a confirmation dialog with email disclaimer if email updates are enabled
   * @param config Configuration for the confirmation dialog
   * @returns Observable<boolean> - true if confirmed, false if cancelled
   */
  confirmWithEmailDisclaimer(config: ConfirmationModalConfig): Observable<boolean> {
    const { title, description } = config;
    const fullDescription = this.getDescriptionWithEmailDisclaimer(description);
    return this.modalManagerService.showConfirmationModal({ title, description: fullDescription });
  }

  /** Returns the description augmented with an email disclaimer if applicable. */
  getDescriptionWithEmailDisclaimer(description: string): string {
    const user = this.userService.user.value?.user;
    const emailUpdatesEnabled = !user?.institution?.settings?.preventEventEmails;
    if (!emailUpdatesEnabled) {
      return description;
    }
    return `${description}<br>${this.translateService.instant(
      'All event attendees and organizers will receive email notifications with updated event details.',
    )}`;
  }

  /**
   * Populates a program admin User object from a list of site admins by matching the admin ID.
   *
   * Flow:
   * 1. Validates that programAdminId exists and siteAdmins array is provided
   * 2. Searches through siteAdmins array to find a user with matching _id
   * 3. Returns the matching User object, or null if not found
   *
   * @param programAdminId - The program admin ID as a string (unpopulated, just the _id)
   * @param siteAdmins - Array of site admins to search in
   * @returns The matching User object, or null if not found or inputs are invalid
   */
  private populateProgramAdmin(
    programAdminId: string | undefined,
    siteAdmins: User[],
  ): User | null {
    // Early return if programAdminId is not provided or siteAdmins is empty
    if (!programAdminId || !siteAdmins?.length) {
      return null;
    }

    // Find and return the user with matching _id
    return siteAdmins.find((user) => user._id === programAdminId) || null;
  }

  /**
   * Fetches site admins for a given site.
   *
   * Flow:
   * 1. Fetches site admins from ProfilesService filtered by site and SiteAdmin role
   * 2. Sorts site admins alphabetically by name (case-insensitive)
   * 3. Returns Observable with sorted site admins array
   *
   * @param siteId - The site ID to fetch admins for
   * @returns Observable with sorted site admins array
   */
  getSiteAdmins(siteId: string): Observable<User[]> {
    if (!siteId) {return of([]);}
    const cached = this.siteAdminsBySiteId.get(siteId);
    if (cached) {
      return of(cached);
    }
    // Fetch only site admins for the selected site
    const filters = {
      sites: [siteId],
      roles: [Personas.SiteAdmin],
    };

    return from(
      this.profilesService.getInstitutionUsers(
        1, // page - always fetch first page
        100, // limit - high enough to cover edge cases but low enough to not impact performance
        filters,
        false, // fetchFreshData - false since site admin lists don't change frequently during a session
      ),
    ).pipe(
      map((result) =>
        // Sort site admins alphabetically by name (case-insensitive)
        result.users.sort((a: User, b: User) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
      ),
      tap((sortedSiteAdmins) => this.siteAdminsBySiteId.set(siteId, sortedSiteAdmins)),
      catchError(() => {
        this.showProgramAdminFetchFailureNotification();
        return of([]); // Return empty array on error
      }),
    );
  }

  /**
   * Fetches site admins for a given site and optionally populates the program admin User object.
   *
   * Flow:
   * 1. Calls getSiteAdmins() to fetch and sort site admins
   * 2. If programAdminId is provided, calls populateProgramAdmin() to find and return the matching User
   * 3. Returns Observable with sorted site admins and populated program admin (if found)
   *
   * @param siteId - The site ID to fetch admins for
   * @param programAdminId - Optional program admin ID to populate
   * @returns Observable with { siteAdmins: User[], programAdmin: User | null }
   */
  getSiteAdminsWithProgramAdmin(
    siteId: string,
    programAdminId: string | undefined,
  ): Observable<{ siteAdmins: User[]; programAdmin: User | null }> {
    return this.getSiteAdmins(siteId).pipe(
      map((sortedSiteAdmins) => {
        // Populate program admin if ID is provided
        const programAdmin = programAdminId
          ? this.populateProgramAdmin(programAdminId, sortedSiteAdmins)
          : null;

        return {
          siteAdmins: sortedSiteAdmins,
          programAdmin,
        };
      }),
    );
  }

  /**
   * Shows an error notification when program admin fetch fails.
   * Uses high z-index toast class to appear above modals.
   */
  showProgramAdminFetchFailureNotification(): void {
    this.notificationService.showDefaultErrorNotificationV2(
      this.translateService.instant('Failed to load site admins'),
      '',
      this.notificationService.generateNotificationCode(),
      undefined, // notificationIconConfig - use default
      undefined, // buttons - none
      undefined, // timeOut - use default
      undefined, // onActivateTick - use default
      undefined, // onTimedOut - default
      TOAST_CLASSES.HIGH_Z_INDEX, // toastClass - high z-index to appear above modals
    );
  }
}
